import React, {useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    useMultiStyleConfig,
    Box,
    Heading,
    Button,
    Text,
    Flex,
    Skeleton
} from '@chakra-ui/react'
import {FormattedMessage, useIntl} from 'react-intl'
import Swatch from '../../components/swatch-group/swatch'
import SwatchGroup from '../../components/swatch-group'
import DisplayPrice from '../../components/display-price'
import Link from '@salesforce/retail-react-app/app/components/link'
import {useDerivedProduct} from '@salesforce/retail-react-app/app/hooks'
import {useAddToCart} from '../../utils/cart-utils'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import DynamicImage from '@app/components/dynamic-image'
import {useProduct} from '@salesforce/commerce-sdk-react'
import {useAddToCartModalContext} from './../../hooks/use-add-to-cart-modal'
import PromoCallout from '../../components/product-tile/promo-callout'
import {getPriceData} from '../../utils/product-utils'

/**
 * SwatchDisplay Component
 *
 * This component is responsible for rendering a group of swatches for a given product attribute,
 * such as color or size. It allows users to select a specific value from the available options.
 *
 * @param {Object} props - The props for the component.
 * @param {Object} props.attribute - The attribute object containing details about the swatch group.
 * @param {string} props.attribute.id - The unique identifier for the attribute.
 * @param {string} props.attribute.name - The display name of the attribute.
 * @param {Array} props.attribute.values - An array of values for the attribute.
 * @param {string} props.attribute.values[].value - The value of the swatch.
 * @param {string} [props.attribute.values[].name] - The display name of the swatch value.
 * @param {Object} [props.attribute.values[].image] - The image object associated with the swatch value.
 * @param {boolean} props.attribute.values[].orderable - Indicates if the swatch value is orderable.
 * @param {string} props.selectedValue - The currently selected value for the attribute.
 * @param {function} props.setSelectedValue - Callback function to update the selected value.
 * @param {Object} props.styles - The styles object for customizing the component's appearance.
 * @param {Object} props.intl - The internationalization object for formatting messages.
 * @param {boolean} [props.isFromProducSetDrawer] - Flag indicating if the component is used within the ProductSetDrawer.
 *
 * @returns {JSX.Element|null} The rendered component or null if no attribute is provided.
 */
const SwatchDisplay = ({
    attribute,
    selectedValue,
    setSelectedValue,
    styles,
    intl,
    isFromProducSetDrawer
}) => {
    if (!attribute) return null
    const displayName =
        attribute.values.find((v) => v.value === selectedValue)?.name || selectedValue
    return (
        <Box {...styles.productSwatches} pt="10px">
            <SwatchGroup
                variant={attribute.id}
                value={selectedValue}
                label={
                    !isFromProducSetDrawer
                        ? intl.formatMessage(
                              {
                                  defaultMessage: '{variantType}',
                                  id: 'product_view.label.variant_type'
                              },
                              {variantType: attribute.name}
                          )
                        : ''
                }
                displayName={displayName}
                handleChange={setSelectedValue}
            >
                {attribute.values.map(({value, name, image, orderable}) => {
                    const content = image ? (
                        <Box
                            backgroundImage={`url(${image?.disBaseLink || image?.link})`}
                            {...styles.colorSwatches}
                        >
                            <Text as="span" className="sr-only">
                                {name || value}
                            </Text>
                        </Box>
                    ) : (
                        <Box>{name || value}</Box>
                    )
                    return (
                        <Swatch
                            key={value}
                            value={value}
                            name={name}
                            variant={attribute.id}
                            selected={selectedValue === value}
                            onSelect={() => setSelectedValue(value)}
                            disabled={!orderable}
                        >
                            {content}
                        </Swatch>
                    )
                })}
            </SwatchGroup>
        </Box>
    )
}

SwatchDisplay.propTypes = {
    attribute: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        values: PropTypes.arrayOf(
            PropTypes.shape({
                value: PropTypes.string.isRequired,
                name: PropTypes.string,
                image: PropTypes.object,
                orderable: PropTypes.bool.isRequired
            })
        ).isRequired
    }),
    selectedValue: PropTypes.string.isRequired,
    setSelectedValue: PropTypes.func.isRequired,
    styles: PropTypes.object.isRequired,
    intl: PropTypes.object.isRequired,
    isFromProducSetDrawer: PropTypes.bool
}

/**
 * ProductSetDrawer Component
 *
 * This component is responsible for rendering a drawer that displays a set of products.
 *
 * @param {Object} props - The props for the component.
 * @param {boolean} props.isOpen - Determines if the drawer is open.
 * @param {function} props.onClose - Callback function to close the drawer.
 * @param {string} props.placement - The placement of the drawer (e.g., 'left', 'right').
 * @param {Array} props.productSet - An array of products to be displayed in the drawer.
 *
 * @returns {JSX.Element} The rendered component.
 */
const ProductSetDrawer = ({isOpen, onClose, placement, productSetData, frontImage}) => {
    const styles = useMultiStyleConfig('ProductViewContainer')
    const intl = useIntl()
    const {variationAttributes} = useDerivedProduct(productSetData, true, false)
    const addToCart = useAddToCart()
    const {onOpen: onAddToCartModalOpen} = useAddToCartModalContext()

    // State for selected color and size
    const [selectedColor, setSelectedColor] = useState(
        productSetData?.variationValues?.color ||
            variationAttributes?.find((attr) => attr.id === 'color')?.values?.[0]?.value ||
            ''
    )
    const [selectedSize, setSelectedSize] = useState(
        productSetData?.variationValues?.size ||
            variationAttributes?.find((attr) => attr.id === 'size')?.values?.[0]?.value ||
            ''
    )

    /**
     * Effect hook to synchronize selected color and size with productSetData.
     *
     * This effect runs whenever the `productSetData` changes. It ensures that the
     * `selectedColor` and `selectedSize` states are updated to reflect the current
     * values in `productSetData`. This is important for maintaining consistency
     * between the displayed product options and the underlying data.
     *
     * @param {Object} productSetData - The product set data containing variation values.
     * @param {string} productSetData.variationValues.color - The color value from the product set data.
     * @param {string} productSetData.variationValues.size - The size value from the product set data.
     */
    useEffect(() => {
        if (productSetData?.variationValues?.color) {
            setSelectedColor(productSetData.variationValues.color)
        }
        if (productSetData?.variationValues?.size) {
            setSelectedSize(productSetData.variationValues.size)
        }
    }, [productSetData])

    // State for the current image
    const [currentImage, setCurrentImage] = useState(frontImage)

    /**
     * Effect hook to update the current image based on the selected color.
     *
     * This effect runs whenever the `selectedColor`, `productSetData`, or `frontImage` changes.
     * It searches for an image group within `productSetData.imageGroups` that matches the selected color
     * and has a `viewType` of "front". If such a group is found, it attempts to find an image within
     * that group with the `viewType` "front" and updates the `currentImage` state with its link.
     * If no "front" image is found, it defaults to the first image in the group. If no matching group
     * is found, it falls back to the default `frontImage`.
     *
     * @param {string} selectedColor - The currently selected color value.
     * @param {Object} productSetData - The product set data containing image groups and other product details.
     * @param {string} frontImage - The default image link to use if no specific image is found.
     */
    useEffect(() => {
        // Find the image group corresponding to the selected color and viewType "front"
        const imageGroup = productSetData?.imageGroups?.find(
            (group) =>
                group.variationAttributes?.some(
                    (attr) =>
                        attr.id === 'color' && attr.values.some((v) => v.value === selectedColor)
                ) && group.viewType === 'front'
        )

        if (imageGroup) {
            // Use the first image in the group as it should be the "front" view
            const [firstImage] = imageGroup.images || []
            if (firstImage) {
                setCurrentImage(firstImage?.disBaseLink)
            }
        } else {
            // If no image group is found, revert to the default image
            setCurrentImage(frontImage)
        }
    }, [selectedColor, productSetData, frontImage])

    // Find master product of the product variation group to retrieve all variants
    const {data: masterProduct} = useProduct({
        parameters: {
            id: productSetData?.master?.masterId,
            allImages: true
        }
    })

    /**
     * Memoized calculation of the selected variant based on selected color and size.
     */
    const selectedVariant = useMemo(() => {
        const baseVariant = masterProduct?.variants?.find((variant) => {
            const variantColor = variant?.variationValues?.color
            const variantSize = variant?.variationValues?.size
            return (
                (!selectedColor || variantColor === selectedColor) &&
                (!selectedSize || variantSize === selectedSize)
            )
        })

        return baseVariant
    }, [selectedColor, selectedSize, masterProduct])

    // Get data of the variant product when color changes
    const {data: currentProductSetData} = useProduct({
        parameters: {
            id: selectedVariant?.productId,
            allImages: true
        }
    })

    const {variationAttributes: derivedVariationAttributes} = useDerivedProduct(
        currentProductSetData,
        true,
        false
    )

    /**
     * Memoized variant data combining selected variant and current product set data.
     *
     * This variable is used to merge the properties of the selected variant with the
     * current product set data fetched from the API. It ensures that the component
     * has access to the most up-to-date and complete information about the selected
     * product variant.
     *
     * @type {Object}
     * @property {string} variantData.id - The unique identifier for the variant.
     * @property {string} variantData.name - The name of the product variant.
     * @property {number} variantData.price - The price of the product variant.
     * @property {string} variantData.currency - The currency of the product price.
     * @property {Array} variantData.imageGroups - The image groups associated with the variant.
     * @property {Object} variantData.variationValues - The variation values (e.g., color, size) for the variant.
     * @property {boolean} variantData.orderable - Indicates if the variant is orderable.
     * @property {Array} variantData.tieredPrices - The tiered pricing information for the variant.
     */
    const variantData = useMemo(() => {
        return {
            ...selectedVariant,
            ...currentProductSetData
        }
    }, [selectedVariant, currentProductSetData])

    // Find color and size attributes
    const colorAttribute = derivedVariationAttributes?.find((attr) => attr.id === 'color')
    const sizeAttribute = derivedVariationAttributes?.find((attr) => attr.id === 'size')

    // check if color is available
    const isColorOrderable = colorAttribute?.values?.some(
        (value) => value.value === selectedColor && value.orderable
    )

    // check if size is available
    const isSizeOrderable = sizeAttribute?.values?.some(
        (value) => value.value === selectedSize && value.orderable
    )

    const priceData = useMemo(() => {
        return getPriceData(variantData)
    }, [variantData])

    useEffect(() => {
        if (isOpen && masterProduct?.variants) {
            // Select a size by default when drawer opens
            const defaultSize = masterProduct.variants[0]?.variationValues?.size
            setSelectedSize(defaultSize)
        }
    }, [isOpen, masterProduct])

    return (
        <Drawer placement={placement} onClose={onClose} isOpen={isOpen} size="lg">
            <DrawerOverlay />
            <DrawerContent minHeight={{base: '50vh', md: 'auto'}}>
                <DrawerCloseButton />
                <DrawerHeader textTransform="uppercase" borderBottom={'none'}>
                    <FormattedMessage
                        id="product_set_drawer.header.quick_shop"
                        defaultMessage="Quick shop"
                    />
                </DrawerHeader>
                <DrawerBody>
                    <Box>
                        {/* -------- MOBILE -------- */}
                        <HideOnDesktop>
                            <Flex direction="row" gap={4} flexWrap="nowrap">
                                {/* Image */}
                                <Box width="100px" flexShrink={0}>
                                    {currentImage && (
                                        <DynamicImage
                                            width="100px"
                                            src={`${currentImage}[?sw={width}]`}
                                            widths={[
                                                '384px',
                                                '384px',
                                                '400px',
                                                '360px',
                                                '480px',
                                                '505px'
                                            ]}
                                            imageProps={{
                                                alt: productSetData?.name,
                                                loading: 'lazy'
                                            }}
                                        />
                                    )}
                                </Box>

                                {/* Title + Price + Color Swatches */}
                                <Box flex="1" pl={'15px'}>
                                    <Heading
                                        as="h2"
                                        fontSize="2xl"
                                        {...styles.productViewHeaderTitle}
                                    >
                                        {productSetData?.name}
                                    </Heading>

                                    <DisplayPrice
                                        priceData={priceData}
                                        currency={variantData?.currency || productSetData?.currency}
                                        promoCallout={
                                            <Skeleton isLoaded={variantData}>
                                                <PromoCallout
                                                    priceData={priceData}
                                                    product={variantData}
                                                />
                                            </Skeleton>
                                        }
                                        {...styles.productPrice}
                                    />

                                    <SwatchDisplay
                                        attribute={colorAttribute}
                                        selectedValue={selectedColor}
                                        setSelectedValue={setSelectedColor}
                                        styles={styles}
                                        intl={intl}
                                    />
                                </Box>
                            </Flex>

                            {/* Size Swatches below in mobile */}
                            <SwatchDisplay
                                isFromProducSetDrawer={true}
                                attribute={sizeAttribute}
                                selectedValue={selectedSize}
                                setSelectedValue={setSelectedSize}
                                styles={styles}
                                intl={intl}
                            />
                        </HideOnDesktop>

                        {/* -------- DESKTOP -------- */}
                        <HideOnMobile>
                            <Flex>
                                {/* Image */}
                                <Box>
                                    {currentImage && (
                                        <DynamicImage
                                            width="180px"
                                            src={`${currentImage}[?sw={width}]`}
                                            widths={[
                                                '384px',
                                                '384px',
                                                '400px',
                                                '360px',
                                                '480px',
                                                '505px'
                                            ]}
                                            imageProps={{
                                                alt: productSetData?.name,
                                                loading: 'lazy'
                                            }}
                                            mr={4}
                                        />
                                    )}
                                </Box>

                                {/* Title + Price + Swatches */}
                                <Box width="384px" paddingLeft="40px">
                                    <Heading
                                        as="h2"
                                        fontSize="4xl"
                                        {...styles.productViewHeaderTitle}
                                    >
                                        {productSetData?.name}
                                    </Heading>

                                    <DisplayPrice
                                        priceData={priceData}
                                        currency={variantData?.currency || productSetData?.currency}
                                        promoCallout={
                                            <Skeleton isLoaded={variantData}>
                                                <PromoCallout
                                                    priceData={priceData}
                                                    product={variantData}
                                                />
                                            </Skeleton>
                                        }
                                        {...styles.productPrice}
                                    />

                                    <SwatchDisplay
                                        attribute={colorAttribute}
                                        selectedValue={selectedColor}
                                        setSelectedValue={setSelectedColor}
                                        styles={styles}
                                        intl={intl}
                                    />

                                    <SwatchDisplay
                                        attribute={sizeAttribute}
                                        selectedValue={selectedSize}
                                        setSelectedValue={setSelectedSize}
                                        styles={styles}
                                        intl={intl}
                                        isFromProducSetDrawer={true}
                                    />
                                </Box>
                            </Flex>
                        </HideOnMobile>

                        {/* -------- Add To Cart Button (common) -------- */}
                        <Box width={{base: '100%', md: '584px'}} pt={{base: '20px', md: '30px'}}>
                            <Button
                                variant="primary"
                                width="100%"
                                onClick={() => {
                                    if (selectedVariant) {
                                        addToCart(variantData, 1).then((itemsAdded) => {
                                            if (itemsAdded) {
                                                onAddToCartModalOpen({
                                                    product: variantData,
                                                    itemsAdded,
                                                    selectedQuantity: 1
                                                })
                                            }
                                        })
                                        onClose()
                                    }
                                }}
                                /**
                                 * Determines if the "Add to Cart" button should be disabled.
                                 *
                                 * The button is disabled if any of the following conditions are true:
                                 * - No variant is selected (`!selectedVariant`).
                                 * - A size attribute is required but no size is selected (`sizeAttribute && !selectedSize`).
                                 * - The selected color is not orderable (`!isColorOrderable`).
                                 * - A size attribute is required but the selected size is not orderable (`sizeAttribute && !isSizeOrderable`).
                                 */
                                disabled={
                                    !selectedVariant ||
                                    (sizeAttribute && !selectedSize) ||
                                    !isColorOrderable ||
                                    (sizeAttribute && !isSizeOrderable)
                                }
                            >
                                <FormattedMessage
                                    id="product_set.button.add_to_cart"
                                    defaultMessage="Add to cart"
                                />
                            </Button>
                            <Box textAlign="center" mt={2}>
                                <Link to={`/product/${productSetData?.id}`}>
                                    <FormattedMessage
                                        id="product_set.link.view_product_details"
                                        defaultMessage="View Product Details"
                                    />
                                </Link>
                            </Box>
                        </Box>
                    </Box>
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    )
}

ProductSetDrawer.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    placement: PropTypes.oneOf(['bottom', 'right']).isRequired,
    productSetData: PropTypes.object,
    frontImage: PropTypes.string
}

export default ProductSetDrawer
